import * as angular from 'angular';
import * as _ from 'lodash';

import { Group, AlarmList, IValueViewModel, Dashboard, Identifier, ObjectAccessRights, Dictionary } from '../../models';
import { Store, StoreElement } from '../../stateManagement';
import { IDashboardService, IStoreService } from '../../services';
import { buildAlarmList } from '../../common/helpers/buildAlarmList';
import { IWidget } from '../../widgets';
import { DashboardStateMapper } from './DashboardStateMapper';

export interface LoadResult {
    groups: Group[];
    alarms: AlarmList;
    viewModels: IValueViewModel[];
    accessRights: ObjectAccessRights[];
}

interface DashboardStateDispatchers {
    addDashboard: (dashboard: Dashboard, filter?: boolean) => angular.IPromise<Dashboard[]>;
    updateDashboard: (dashboard: Dashboard, filter?: boolean) => angular.IPromise<Dashboard[]>;
    loadDashboard: (guid: string) => angular.IPromise<Dashboard>;
    /**
     * Refreshes the store after deleting a widget.
     * Why everything is not handled at store level, I don't know...
     * @param dashboardGuid The guid of the dashboard to refresh.
     */
    postDeleteWidget(dashboardGuid: string): angular.IPromise<Dashboard>
}

export interface DashboardStateConnectResult {
    unsubscribe: () => void;
    onLoad: angular.IPromise<void>;
}

export interface IDashboardStateService extends DashboardStateDispatchers {
    connect: (scope: DashboardStateMapper) => DashboardStateConnectResult;
}

export class DashboardStateService implements IDashboardStateService {
    static serviceId = 'dashboardStateService';

    private _onLoad: angular.IDeferred<void>;

    static $inject = [
        '$q',
        'storeService',
        'dashboardService'
    ];

    constructor(
        private _$q: angular.IQService,
        private _storeService: IStoreService,
        private _dashboardService: IDashboardService
    ) {
    }

    connect(scope: DashboardStateMapper): DashboardStateConnectResult {
        // Serve to promise the first resolve
        this._onLoad = this._$q.defer<void>();
        scope.updateValidationPage = (type: string, referenceGroup: string, widgets: IWidget[]) => this._storeService.dispatchers().validationPageConfigurations.updateReferenceGroup(type, referenceGroup, widgets);

        const unsubscribe = this._storeService.register(store => {
            this.mapStateToThis(store, scope);
        }, [
            StoreElement.Actions,
            StoreElement.Alarms,
            StoreElement.ConnectionPoints,
            StoreElement.Dashboards,
            StoreElement.DigitalSets,
            StoreElement.Groups,
            StoreElement.ObjectAccessRights,
            StoreElement.Tags,
            StoreElement.ValidationPageConfigurations,
            StoreElement.ValueViewModels
        ]);

        return {
            unsubscribe,
            onLoad: this._onLoad.promise
        };
    }

    addDashboard(dashboard: Dashboard, filter?: boolean): angular.IPromise<Dashboard[]> {
        return this._storeService.dispatchers().dashboards.addOne(dashboard, filter);
    }

    updateDashboard(dashboard: Dashboard, filter?: boolean): angular.IPromise<Dashboard[]> {
        return this._storeService.dispatchers().dashboards.updateOne(dashboard, filter)
            .then(data => {
                data = data.map(d => this._dashboardService.deserializeDashboard(d));
                return data;
            });
    }

    /**
     * 
     * @param guid
     */
    loadDashboard(guid: string): angular.IPromise<Dashboard> {
        return this._storeService.dispatchers().dashboards.getOne(guid);
    }

    postDeleteWidget(guid: string): angular.IPromise<Dashboard> {
        const { dashboards, valueViewModels } = this._storeService.dispatchers();
        return this._$q.all([dashboards.getOne(guid), valueViewModels.getAll(true)])
            .then(data => {
                return data[0];
            });
    }

    /**
     * Watch out that dashboard state does not, in fact, use store for rendering.
     * What is displayed is from widgetlist, which is not computed from here
     */
    private mapStateToThis(store: Store, scope: DashboardStateMapper) {
        const dashboards: Dashboard[] = _.values(store.dashboards.dictionary);
        const groupDictionary = store.groups.dictionary;
        const groups: Group[] = _.values(store.groups.dictionary);
        const identifiers: Identifier[] = _.values(store.identifiers.dictionary);
        const viewModels = store.valueViewModels.dictionary;
        const accessRights: ObjectAccessRights[] = _.values(store.objectAccessRights.dictionary);
        const tagsDictionary = angular.copy(store.tags.dictionary);
        const connectionPointsDictionary = angular.copy(store.connectionPoints.dictionary);
        const alarmList = this.buildAlarmList(store.alarms.dictionary, viewModels);
        const digitalSets = store.digitalSetDictionary.dictionary;
        const actions = store.actions.dictionary;
        const validationPageConfigurations = store.validationPageConfigurations.dictionary;

        const toMap: DashboardStateMapper = {
            accessRights,
            actions,
            alarmList,
            connectionPointsDictionary,
            dashboards,
            digitalSets,
            groupDictionary,
            groups,
            identifiers,
            store,
            tagsDictionary,
            viewModels,
            validationPageConfigurations
        };

        (Object as any).assign(scope, toMap);
        this._onLoad.resolve();
    }

    private buildAlarmList(alarmList: AlarmList, viewModels: Dictionary<IValueViewModel>): AlarmList {
        return buildAlarmList(alarmList, viewModels);
    }
}